import { useState, type FormEvent } from "react";
import { KeyRound, ShieldCheck, ShieldOff } from "lucide-react";
import { useAuth } from "@/lib/auth";

export function SecurityPage() {
  const {
    user,
    mfaFactorId,
    mfaRequired,
    enrollMfa,
    confirmMfaEnrollment,
  } = useAuth();
  const [enrollment, setEnrollment] = useState<{ factorId: string; qr: string; secret: string } | null>(
    null,
  );
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const enrolled = Boolean(mfaFactorId) && !mfaRequired;

  async function startEnrollment() {
    setBusy(true);
    setError(null);
    try {
      setEnrollment(await enrollMfa());
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start enrollment");
    } finally {
      setBusy(false);
    }
  }

  async function confirm(event: FormEvent) {
    event.preventDefault();
    if (!enrollment) return;
    setBusy(true);
    setError(null);
    try {
      await confirmMfaEnrollment(enrollment.factorId, code.trim());
      setEnrollment(null);
      setCode("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code did not work");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h2 style={{ fontSize: 15 }}>Account security</h2>
          <p>Staff accounts must use a time-based one-time password.</p>
        </div>
        <span className={`badge ${enrolled ? "badge-mint" : "badge-sunshine"}`}>
          {enrolled ? <ShieldCheck size={12} /> : <ShieldOff size={12} />}
          {enrolled ? "2FA active" : "2FA not enrolled"}
        </span>
      </div>

      <div className="grid grid-2">
        <section className="card card-pad stack">
          <div className="row">
            <span
              className="kpi-icon"
              style={{ background: "var(--grape-wash)", color: "var(--grape-ink)" }}
            >
              <KeyRound size={16} />
            </span>
            <div>
              <h2 style={{ fontSize: 17 }}>Authenticator app</h2>
              <p className="small muted">{user?.email}</p>
            </div>
          </div>

          {enrolled ? (
            <p className="small muted">
              This account already has a verified authenticator. To replace it, remove the
              factor in Supabase Auth and enroll again from here.
            </p>
          ) : enrollment ? (
            <form className="stack" onSubmit={confirm}>
              <p className="small muted">
                Scan this code with Google Authenticator, 1Password or Authy, then enter the
                six-digit code.
              </p>
              <div
                className="mfa-qr"
                style={{ display: "grid", placeItems: "center" }}
                dangerouslySetInnerHTML={{ __html: enrollment.qr }}
              />
              <div className="mfa-secret mono">{enrollment.secret}</div>
              <input
                className="input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="000000"
                value={code}
                onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
                style={{ textAlign: "center", fontSize: 20, letterSpacing: 6, fontWeight: 700 }}
              />
              {error && <div className="login-error">{error}</div>}
              <div className="row" style={{ justifyContent: "flex-end" }}>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setEnrollment(null);
                    setCode("");
                    setError(null);
                  }}
                >
                  Cancel
                </button>
                <button className="btn btn-primary" disabled={busy || code.length !== 6}>
                  {busy ? "Verifying…" : "Verify and enable"}
                </button>
              </div>
            </form>
          ) : (
            <>
              <p className="small muted">
                Without an authenticator, a leaked password is enough to read every learner's
                data. Enrollment takes about a minute.
              </p>
              {error && <div className="login-error">{error}</div>}
              <button
                className="btn btn-primary"
                style={{ width: "fit-content" }}
                disabled={busy}
                onClick={() => void startEnrollment()}
              >
                <KeyRound size={14} />
                {busy ? "Preparing…" : "Set up authenticator"}
              </button>
            </>
          )}
        </section>

        <section className="card card-pad stack">
          <h2 style={{ fontSize: 17 }}>How access works</h2>
          <div className="stat-line">
            <span className="label">Console access</span>
            <span className="value">admin_users table</span>
          </div>
          <div className="stat-line">
            <span className="label">Roles</span>
            <span className="value">admin · moderator · analyst</span>
          </div>
          <div className="stat-line">
            <span className="label">Session</span>
            <span className="value">Supabase Auth, AAL2</span>
          </div>
          <div className="stat-line">
            <span className="label">Audit trail</span>
            <span className="value">audit_log, staff-readable</span>
          </div>
          <p className="tiny faint">
            Every sensitive action in this console writes to <span className="mono">audit_log</span>.
            Granting roles happens in SQL, never from the browser.
          </p>
        </section>
      </div>
    </>
  );
}
